import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, HandMetal, Ticket, Grape, ChefHat, Wine } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Experiencia } from "../components/Experiencia";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd, faqLd } from "../components/SEO";

const TAB_IDS = ["menu", "cultura", "gastro", "bar"];

const TabBar = ({ tabs, active, onChange, testid }) => (
    <div className="flex flex-wrap justify-center gap-2 sm:gap-6 border-b border-[#c9a24b]/20" data-testid={testid}>
        {tabs.map((tab) => (
            <button
                key={tab.id}
                onClick={() => onChange(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`font-caps text-xs sm:text-sm tracking-[0.2em] uppercase px-4 py-3 -mb-px border-b-2 transition-colors duration-300 ${
                    active === tab.id ? "border-[#c9a24b] text-[#c9a24b]" : "border-transparent text-[#efe6d6]/55 hover:text-[#efe6d6]"
                }`}
            >
                {tab.label}
            </button>
        ))}
    </div>
);

const BarTab = () => {
    const { t } = useLanguage();
    return (
        <div className="mx-auto max-w-[1100px] px-5 sm:px-10 py-16" data-testid="bar-tab-content">
            <Reveal>
                <div className="max-w-xl mx-auto border border-[#c9a24b]/20 bg-[#0a0705]/85 backdrop-blur-sm p-8 sm:p-10 text-center" data-testid="bar-card">
                    <h2 className="font-display font-medium gold-text text-3xl sm:text-4xl tracking-[0.12em]" data-testid="bar-titulo">BAR</h2>
                    <div className="flex justify-center mt-3">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-28 opacity-90" />
                    </div>
                    <p className="mt-5 font-display italic gold-text text-xl sm:text-2xl" data-testid="bar-nome">{t.experiencia.bar.nome}</p>
                    <p className="mt-3 font-light text-[#efe6d6]/60 text-xs sm:text-sm" data-testid="bar-cargo">
                        {t.experiencia.bar.cargoA}
                        <span className="block font-caps text-[11px] sm:text-xs tracking-[0.2em] uppercase text-[#d19a3f] mt-1 not-italic">{t.experiencia.bar.cargoB}</span>
                    </p>

                    <div className="mt-8 pt-6 border-t border-[#c9a24b]/25">
                        <div className="flex items-center justify-center gap-3 mb-6">
                            <Clock size={18} strokeWidth={1.4} className="text-[#d19a3f]" />
                            <h3 className="font-caps text-xs sm:text-sm tracking-[0.25em] uppercase text-[#d19a3f]" data-testid="bar-horario-titulo">{t.experiencia.bar.horarioTitulo}</h3>
                        </div>
                        <div className="divide-y divide-[#c9a24b]/15">
                            {t.experiencia.bar.blocos.map((b, i) => (
                                <div key={b.horas} className="py-5 first:pt-0 last:pb-0" data-testid={`bar-bloco-${i}`}>
                                    <p className="font-display font-medium gold-text text-2xl sm:text-3xl tracking-[0.04em]" data-testid={`bar-horas-${i}`}>{b.horas}</p>
                                    <p className="mt-1.5 font-caps text-[11px] sm:text-xs tracking-[0.3em] uppercase text-[#efe6d6]" data-testid={`bar-estado-${i}`}>{b.estado}</p>
                                    <p className="mt-2 font-light text-[#efe6d6]/65 text-sm leading-relaxed max-w-md mx-auto" data-testid={`bar-nota-${i}`}>{b.nota}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#c9a24b]/25 flex items-center justify-center gap-3">
                        <Wine size={18} strokeWidth={1.4} className="text-[#d19a3f] shrink-0" />
                        <p className="font-light text-[#efe6d6]/60 text-xs sm:text-sm" data-testid="bar-regra">{t.experiencia.bar.regra}</p>
                    </div>
                </div>
            </Reveal>
        </div>
    );
};

const CulturaTradicao = ({ onVerBar }) => {
    const { t } = useLanguage();
    return (
        <div className="mx-auto max-w-[1100px] px-5 sm:px-10 py-16" data-testid="cultura-tab-content">
            <Reveal className="text-center">
                <h2 className="font-display font-medium gold-text text-3xl sm:text-4xl tracking-[0.06em]">{t.experiencia.culturaTitulo}</h2>
                <div className="flex justify-center mt-3">
                    <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-36 opacity-90" />
                </div>
            </Reveal>

            <div className="mt-12 grid lg:grid-cols-2 gap-12">
                <Reveal>
                    <div className="border border-[#c9a24b]/20 bg-[#0a0705] p-8 sm:p-10" data-testid="lineup-card">
                        <div className="flex items-center gap-3 mb-6">
                            <Clock size={20} strokeWidth={1.4} className="text-[#d19a3f]" />
                            <h3 className="font-caps text-sm tracking-[0.25em] uppercase text-[#d19a3f]">{t.experiencia.lineupTitulo}</h3>
                        </div>
                        <div className="space-y-4">
                            {t.experiencia.lineup.map((l, i) => (
                                <div key={`${l.hora}-${i}`} className="flex items-baseline gap-5" data-testid={`lineup-${i}`}>
                                    <span className="font-display gold-text text-lg w-16 shrink-0">{l.hora}</span>
                                    <span className="h-px w-4 bg-[#c9a24b]/40 self-center shrink-0" />
                                    <span className="font-light text-[#efe6d6]/75 text-sm sm:text-base">
                                        {l.momento}
                                        {l.link && (
                                            <>
                                                {" "}
                                                <button type="button" onClick={onVerBar} className="text-[#c9a24b] underline underline-offset-4 hover:text-[#ecd49b] transition-colors duration-300" data-testid={`lineup-link-${i}`}>
                                                    {t.experiencia.lineupLink}
                                                </button>
                                            </>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs font-light text-[#efe6d6]/40 mt-6">{t.experiencia.lineupNota}</p>
                    </div>
                </Reveal>

                <div className="space-y-6">
                    <Reveal delay={0.1}>
                        <div className="border border-[#c9a24b]/20 bg-[#0a0705] p-8" data-testid="etiqueta-card">
                            <div className="flex items-center gap-3 mb-4">
                                <HandMetal size={20} strokeWidth={1.4} className="text-[#d19a3f]" />
                                <h3 className="font-caps text-sm tracking-[0.25em] uppercase text-[#d19a3f]">{t.experiencia.etiquetaTitulo}</h3>
                            </div>
                            <p className="font-light text-[#efe6d6]/75 leading-relaxed text-sm sm:text-base">
                                {t.experiencia.etiquetaTexto}
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <div className="border border-[#c9a24b]/20 bg-[#0a0705] p-8" data-testid="bilhete-card">
                            <div className="flex items-center gap-3 mb-4">
                                <Ticket size={20} strokeWidth={1.4} className="text-[#d19a3f]" />
                                <h3 className="font-caps text-sm tracking-[0.25em] uppercase text-[#d19a3f]">{t.experiencia.bilheteTitulo}</h3>
                            </div>
                            <ul className="space-y-2.5">
                                {t.experiencia.incluido.map((item) => (
                                    <li key={item} className="flex items-center gap-3 font-light text-[#efe6d6]/75 text-sm sm:text-base">
                                        <span className="w-1.5 h-1.5 rotate-45 border border-[#c9a24b] shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs font-light text-[#efe6d6]/40 mt-5">{t.experiencia.bebidas}</p>
                            <p className="text-xs font-light text-[#efe6d6]/60 mt-3">
                                {t.experiencia.notaB}
                                <span className="text-[#ecd49b]">{t.experiencia.prazoData}</span>.
                            </p>
                            <p className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b] mt-4">{t.experiencia.exclusivo}</p>
                            <Link
                                to="/confirmar"
                                data-testid="bilhete-confirmar-button"
                                className="inline-block mt-5 font-caps text-[10px] tracking-[0.3em] uppercase text-[#070503] bg-[#c9a24b] px-6 py-3 hover:bg-[#ecd49b] transition-colors duration-400"
                            >
                                {t.experiencia.confirmar}
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    );
};

const GastronomiaVinhos = () => {
    const { t } = useLanguage();
    return (
        <div className="mx-auto max-w-[1100px] px-5 sm:px-10 py-16" data-testid="gastro-tab-content">
            <Reveal className="text-center">
                <h2 className="font-display font-medium gold-text text-3xl sm:text-4xl tracking-[0.06em]">{t.experiencia.gastroTitulo}</h2>
                <div className="flex justify-center mt-3">
                    <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-36 opacity-90" />
                </div>
            </Reveal>
            <div className="mt-12 grid lg:grid-cols-2 gap-12">
                <Reveal>
                    <div className="border border-[#c9a24b]/20 bg-[#0a0705] p-8 sm:p-10 h-full" data-testid="gastronomia-card">
                        <div className="flex items-center gap-3 mb-4">
                            <ChefHat size={20} strokeWidth={1.4} className="text-[#d19a3f]" />
                            <h3 className="font-caps text-sm tracking-[0.25em] uppercase text-[#d19a3f]">{t.experiencia.gastroCardTitulo}</h3>
                        </div>
                        <p className="font-light text-[#efe6d6]/75 leading-relaxed text-sm sm:text-base">
                            {t.experiencia.gastroP1}
                        </p>
                        <p className="font-light text-[#efe6d6]/75 leading-relaxed text-sm sm:text-base mt-4">
                            {t.experiencia.gastroP2}
                        </p>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="border border-[#c9a24b]/20 bg-[#0a0705] p-8 sm:p-10 h-full" data-testid="vinhos-card">
                        <div className="flex items-center gap-3 mb-4">
                            <Grape size={20} strokeWidth={1.4} className="text-[#d19a3f]" />
                            <h3 className="font-caps text-sm tracking-[0.25em] uppercase text-[#d19a3f]">{t.experiencia.vinhosCardTitulo}</h3>
                        </div>
                        <p className="font-light text-[#efe6d6]/75 leading-relaxed text-sm sm:text-base">
                            {t.experiencia.vinhosP1}
                        </p>
                        <p className="font-light text-[#efe6d6]/75 leading-relaxed text-sm sm:text-base mt-4">
                            {t.experiencia.vinhosP2}
                        </p>
                    </div>
                </Reveal>
            </div>
            <Reveal className="mt-12 text-center">
                <p className="font-display italic font-light text-xl text-[#efe6d6]/70">{t.experiencia.exclusivo}.</p>
            </Reveal>
        </div>
    );
};

export default function ExperienciaPage() {
    const [searchParams] = useSearchParams();
    const tabInicial = TAB_IDS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "menu";
    const [tab, setTab] = useState(tabInicial);
    const { t } = useLanguage();
    useSEO({
        title: t.seo.experiencia.title,
        description: t.seo.experiencia.desc,
        jsonLd: [
            breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.experiencia, path: "/experiencia" }]),
            faqLd(t.faq.items),
        ],
    });
    const tabs = TAB_IDS.map((id) => ({ id, label: t.experiencia.tabs[id] }));

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative" data-testid="experiencia-page">
            {/* Fundo fixo: a imagem fica parada e a informação rola por cima */}
            <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
                <img loading="eager" decoding="async" src="/images/experiencia-fundo.webp" alt="" className="w-full h-full object-cover object-center" data-testid="experiencia-fundo" />
                <div className="absolute inset-0 bg-[#070503]/40" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 pt-44"
            >
                <div className="mx-auto max-w-[1100px] px-5 sm:px-10">
                    <TabBar tabs={tabs} active={tab} onChange={setTab} testid="experiencia-tabs" />
                </div>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={tab}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4 }}
                    >
                        {tab === "menu" && <Experiencia />}
                        {tab === "cultura" && <CulturaTradicao onVerBar={() => setTab("bar")} />}
                        {tab === "gastro" && <GastronomiaVinhos />}
                        {tab === "bar" && <BarTab />}
                    </motion.div>
                </AnimatePresence>

                <Reveal className="mx-auto max-w-[900px] px-5 sm:px-10 pb-24">
                    <div className="border-t border-[#c9a24b]/20 pt-14">
                        <h2 className="font-caps gold-text text-lg sm:text-xl tracking-[0.2em] text-center" data-testid="faq-title">
                            {t.faq.titulo}
                        </h2>
                        <div className="flex justify-center mt-2 mb-10">
                            <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-36 opacity-90" loading="lazy" />
                        </div>
                        <div className="space-y-5">
                            {t.faq.items.map((f, i) => (
                                <div key={i} className="border border-[#c9a24b]/20 bg-[#0a0705] p-6 sm:p-8" data-testid={`faq-${i}`}>
                                    <h3 className="font-display font-medium text-lg sm:text-xl text-[#efe6d6]">{f.q}</h3>
                                    <p className="font-light text-[#efe6d6]/65 text-sm sm:text-base leading-relaxed mt-3">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Reveal>
            </motion.main>
        </div>
    );
}
